import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    AlertTriangle,
    Ban,
    CalendarClock,
    Download,
    ExternalLink,
    Info,
    Megaphone,
    MessageCircle,
    RefreshCw,
    Search,
    Undo2,
} from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useToast } from '../../../src/contexts/ToastContext';
import ConfirmModal from '../../../src/components/ConfirmModal';
import LoadingSpinner from '../../../src/components/LoadingSpinner';
import NegotiationDetailModal from '../components/NegotiationDetails/NegotiationDetailModal';
import { logNegotiationAction } from '../../../src/lib/negotiationLogger';
import { logUsageEvent } from '../../../src/lib/logger';
import { useMotivosPerda } from '../../../src/hooks/useMotivosPerda';
import {
    MOTIVOS_DESCARTE,
    OPCOES_ADIAR_DIAS,
    ROTULO_SITUACAO,
    ROTULO_VIA,
    contarPorSituacao,
    csvPublicoMeta,
    descarteBloqueiaContato,
    diasDesde,
    formatarTelefone,
    linkWhatsApp,
    montarMensagem,
    situacaoRemarketing,
    visivelNaLista,
    type LinhaRemarketing,
    type SituacaoRemarketing,
} from '../../../src/lib/remarketing';

/**
 * Remarketing de perdidos (#6419, pedido da Talita). Negociações perdidas por motivo que
 * ainda pode voltar ("Sem retorno", "Não tem interesse"…) caem aqui sozinhas, pelo gatilho
 * do banco. A BDR reaborda 1 a 1 pelo WhatsApp e exporta o público para anúncio no Meta.
 * Sem disparo automático (decisão do Felipe, 30/10/2026).
 */

const POR_PAGINA = 50;
/** O PostgREST corta cada resposta em 1.000 linhas: a lista (~2 mil) vem em lotes. */
const LOTE_BANCO = 1000;
const ABAS: SituacaoRemarketing[] = ['pronto', 'aguardando', 'em_nutricao', 'recuperado', 'descartado'];

const COR_MOTIVO: Record<string, string> = {
    'Sem retorno': 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800/50',
    'Não tem interesse': 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-700/40 dark:text-slate-200 dark:border-slate-600',
    'Fechou com outra empresa': 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/20 dark:text-violet-300 dark:border-violet-800/50',
    'Acha que é golpe': 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800/50',
    'Pediu para falar depois': 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-900/20 dark:text-sky-300 dark:border-sky-800/50',
};
const COR_MOTIVO_PADRAO = 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-700/40 dark:text-gray-200 dark:border-gray-600';

const dataCurta = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '');
const haDias = (iso: string, agora: Date) => {
    const d = diasDesde(iso, agora);
    return d === 0 ? 'hoje' : d === 1 ? 'há 1 dia' : `há ${d} dias`;
};

type Acao = { tipo: 'voltar' | 'descartar' | 'adiar'; linha: LinhaRemarketing } | null;

const RemarketingView: React.FC = () => {
    const { profile, user } = useAuth();
    const { addToast } = useToast();
    const { motivos } = useMotivosPerda();

    const [linhas, setLinhas] = useState<LinhaRemarketing[]>([]);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState<string | null>(null);
    const [semTabela, setSemTabela] = useState(false);
    const [agora, setAgora] = useState(() => new Date());

    const [aba, setAba] = useState<SituacaoRemarketing>('pronto');
    const [motivoFiltro, setMotivoFiltro] = useState('');
    const [origemFiltro, setOrigemFiltro] = useState('');
    const [busca, setBusca] = useState('');
    const [limite, setLimite] = useState(POR_PAGINA);

    const [acao, setAcao] = useState<Acao>(null);
    const [motivoDescarte, setMotivoDescarte] = useState<string>(MOTIVOS_DESCARTE[0]);
    const [diasAdiar, setDiasAdiar] = useState<number>(30);
    const [salvando, setSalvando] = useState(false);
    const [negociacaoAberta, setNegociacaoAberta] = useState<number | null>(null);

    const carregar = useCallback(async () => {
        setCarregando(true);
        setErro(null);
        try {
            const todas: LinhaRemarketing[] = [];
            for (let de = 0; ; de += LOTE_BANCO) {
                const { data, error } = await supabase
                    .from('remarketing_lista')
                    .select('*')
                    .order('perdido_em', { ascending: false })
                    .order('negociacao_id', { ascending: false })
                    .range(de, de + LOTE_BANCO - 1);
                if (error) throw error;
                todas.push(...((data ?? []) as LinhaRemarketing[]));
                if (!data || data.length < LOTE_BANCO) break;
            }
            setLinhas(todas);
            setSemTabela(false);
        } catch (e: any) {
            const msg = String(e?.message ?? e);
            // View ainda não criada (migration não aplicada): aviso em vez de erro técnico.
            if (e?.code === '42P01' || e?.code === 'PGRST205' || /remarketing_lista/.test(msg)) setSemTabela(true);
            else setErro(msg);
        } finally {
            setAgora(new Date());
            setCarregando(false);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);
    // Volta para a primeira página ao trocar de aba ou filtro.
    useEffect(() => { setLimite(POR_PAGINA); }, [aba, motivoFiltro, origemFiltro, busca]);

    const motivoDe = useCallback((motivo: string) => motivos.find(m => m.motivo === motivo), [motivos]);

    const filtradas = useMemo(() => {
        const termo = busca.trim().toLocaleLowerCase('pt-BR');
        const digitos = termo.replace(/\D/g, '');
        return linhas.filter(l => {
            if (!visivelNaLista(l)) return false;
            if (motivoFiltro && l.motivo_perda !== motivoFiltro) return false;
            if (origemFiltro && (l.origem || 'Sem origem') !== origemFiltro) return false;
            if (termo) {
                const nome = (l.cliente_nome ?? '').toLocaleLowerCase('pt-BR');
                const fone = l.telefone ?? '';
                if (!nome.includes(termo) && !(digitos.length >= 4 && fone.includes(digitos))) return false;
            }
            return true;
        });
    }, [linhas, motivoFiltro, origemFiltro, busca]);

    const contagem = useMemo(() => contarPorSituacao(filtradas, agora), [filtradas, agora]);

    const daAba = useMemo(() => {
        const lista = filtradas.filter(l => situacaoRemarketing(l, agora) === aba);
        const t = (iso: string | null) => (iso ? new Date(iso).getTime() : 0);
        // Prontos: perda mais recente primeiro (lead fresco responde mais). Aguardando: quem
        // fica pronto antes. Em nutrição: quem está há mais tempo sem nova mensagem.
        if (aba === 'aguardando') lista.sort((a, b) => t(a.reabordar_em) - t(b.reabordar_em));
        else if (aba === 'em_nutricao') lista.sort((a, b) => t(a.ultima_tentativa_em) - t(b.ultima_tentativa_em));
        else if (aba === 'recuperado') lista.sort((a, b) => t(b.recuperado_em) - t(a.recuperado_em));
        else if (aba === 'descartado') lista.sort((a, b) => t(b.updated_at) - t(a.updated_at));
        return lista;
    }, [filtradas, aba, agora]);

    const ocultos = useMemo(
        () => linhas.filter(l => (l.etapa === 'na_fila' || l.etapa === 'em_nutricao') && !visivelNaLista(l)).length,
        [linhas],
    );
    const origens = useMemo(() => [...new Set(linhas.map(l => l.origem || 'Sem origem'))].sort(), [linhas]);
    const motivosNaLista = useMemo(() => {
        const daLista = motivos.filter(m => m.vai_para_remarketing).map(m => m.motivo);
        return [...new Set([...daLista, ...linhas.map(l => l.motivo_perda)])];
    }, [motivos, linhas]);
    const regras = motivos.filter(m => m.vai_para_remarketing && m.ativo);

    const atualizarLocal = (id: number, patch: Partial<LinhaRemarketing>) =>
        setLinhas(prev => prev.map(l => (l.negociacao_id === id ? { ...l, ...patch } : l)));

    const gravar = async (l: LinhaRemarketing, patch: Partial<LinhaRemarketing>, log: string, detalhes?: Record<string, unknown>) => {
        const { error } = await supabase.from('remarketing_leads').update(patch).eq('negociacao_id', l.negociacao_id);
        if (error) throw error;
        atualizarLocal(l.negociacao_id, patch);
        // "Pronto" ou "aguardando" sai da comparação com agora: sem atualizar o relógio,
        // quem voltou para a fila com data de agora apareceria em Aguardando.
        setAgora(new Date());
        await logNegotiationAction(l.negociacao_id, user?.id, log, { motivo_perda: l.motivo_perda, ...detalhes });
    };

    const enviarWhatsApp = async (l: LinhaRemarketing) => {
        const texto = montarMensagem(motivoDe(l.motivo_perda)?.roteiro, l.cliente_nome, profile?.full_name);
        const link = linkWhatsApp(l.telefone, texto);
        if (!link) return;
        // Abre antes de qualquer espera: depois de um await o navegador bloqueia a janela.
        window.open(link, '_blank', 'noopener,noreferrer');
        try {
            await gravar(l, {
                etapa: 'em_nutricao',
                tentativas: l.tentativas + 1,
                ultima_tentativa_em: new Date().toISOString(),
            }, 'remarketing: mensagem de reabordagem', { tentativa: l.tentativas + 1 });
            logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'remarketing', acao: 'mensagem_reabordagem', origem: 'acao' });
        } catch (e: any) {
            addToast(`A conversa abriu, mas a tentativa não foi registrada: ${e?.message ?? e}`, 'error');
        }
    };

    const desfazerDescarte = async (l: LinhaRemarketing) => {
        try {
            await gravar(l, {
                etapa: 'na_fila', descarte_motivo: null, nao_contatar: false, reabordar_em: new Date().toISOString(),
            }, 'remarketing: voltou para a fila');
            addToast('Voltou para a fila de reabordagem.', 'success');
        } catch (e: any) {
            addToast(`Não foi possível desfazer: ${e?.message ?? e}`, 'error');
        }
    };

    const confirmarAcao = async () => {
        if (!acao) return;
        const l = acao.linha;
        setSalvando(true);
        try {
            if (acao.tipo === 'voltar') {
                const { error } = await supabase.rpc('remarketing_voltar_para_funil', { p_negociacao_id: l.negociacao_id });
                if (error) throw error;
                atualizarLocal(l.negociacao_id, { etapa: 'recuperado', recuperado_via: 'remarketing', recuperado_em: new Date().toISOString() });
                logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'remarketing', acao: 'voltar_para_funil', origem: 'acao' });
                addToast('Negociação de volta ao funil, no seu nome.', 'success');
            } else if (acao.tipo === 'descartar') {
                await gravar(l, {
                    etapa: 'descartado', descarte_motivo: motivoDescarte, nao_contatar: descarteBloqueiaContato(motivoDescarte),
                }, 'remarketing: descartada', { descarte: motivoDescarte });
                addToast('Descartada da lista.', 'success');
            } else {
                const quando = new Date(Date.now() + diasAdiar * 86_400_000);
                await gravar(l, { etapa: 'na_fila', reabordar_em: quando.toISOString() },
                    'remarketing: reabordagem adiada', { para: quando.toLocaleDateString('pt-BR') });
                addToast(`Reabordagem adiada para ${quando.toLocaleDateString('pt-BR')}.`, 'success');
            }
            setAgora(new Date());
            setAcao(null);
        } catch (e: any) {
            addToast(`Não foi possível concluir: ${e?.message ?? e}`, 'error');
        } finally {
            setSalvando(false);
        }
    };

    const exportarMeta = () => {
        const { csv, total } = csvPublicoMeta(daAba);
        if (total === 0) {
            addToast('Nenhum telefone válido nesta aba para exportar.', 'warning');
            return;
        }
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `publico-remarketing-${aba}-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'remarketing', acao: 'exportar_publico_meta', origem: 'acao' });
        addToast(`${total} telefone(s) exportado(s). No Meta: Públicos > Criar público > Lista de clientes.`, 'success');
    };

    const visiveis = daAba.slice(0, limite);

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-4 sm:p-5 space-y-4">
            {/* Cabeçalho */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="flex items-center gap-2.5">
                    <div className="p-2 rounded-xl bg-secondary/10 text-secondary">
                        <Megaphone size={18} />
                    </div>
                    <div>
                        <h3 className="text-sm font-bold text-gray-900 dark:text-white">Remarketing de perdidos</h3>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400">
                            Perdidos que ainda podem voltar. Entram aqui sozinhos pelo motivo da perda.
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={exportarMeta}
                        disabled={carregando || daAba.length === 0}
                        title="Baixa os telefones desta aba (com os filtros) no formato da lista de clientes do Meta"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-full border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
                    >
                        <Download size={13} /> Público para o Meta
                    </button>
                    <button
                        onClick={carregar}
                        className="p-2 rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        title="Recarregar a lista"
                        aria-label="Recarregar a lista"
                    >
                        <RefreshCw size={16} className={carregando ? 'animate-spin' : ''} />
                    </button>
                </div>
            </div>

            {/* Regras: o que entra e quando */}
            {regras.length > 0 && (
                <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-700/40 text-[11px] text-gray-600 dark:text-gray-300">
                    <Info size={13} className="mt-0.5 shrink-0 text-gray-400" aria-hidden="true" />
                    <p>
                        <span className="font-semibold">Entram sozinhos os perdidos por: </span>
                        {regras.map(m => `${m.motivo} (depois de ${m.esperar_dias} dias)`).join(' · ')}.
                        {ocultos > 0 && (
                            <> {ocultos} {ocultos === 1 ? 'ficou oculto' : 'ficaram ocultos'} por já ter outra negociação ativa ou ser a mesma pessoa perdida mais de uma vez.</>
                        )}
                    </p>
                </div>
            )}

            {semTabela ? (
                <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                    <AlertTriangle size={16} className="text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-800 dark:text-amber-200">
                        A lista de Remarketing ainda não está ativa no sistema. Fale com a Tecnologia.
                    </p>
                </div>
            ) : erro ? (
                <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                    <AlertTriangle size={16} className="text-red-500 shrink-0 mt-0.5" />
                    <p className="text-xs text-red-700 dark:text-red-300">Não foi possível carregar a lista: {erro}</p>
                </div>
            ) : (
                <>
                    {/* Abas */}
                    <div className="flex gap-1 p-1 rounded-2xl bg-gray-100 dark:bg-gray-700 overflow-x-auto" role="tablist" aria-label="Situação na lista">
                        {ABAS.map(s => (
                            <button
                                key={s}
                                role="tab"
                                aria-selected={aba === s}
                                onClick={() => setAba(s)}
                                className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-xl transition-colors ${
                                    aba === s
                                        ? 'bg-white dark:bg-gray-800 text-primary dark:text-primary-light shadow-sm'
                                        : 'text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-white'
                                }`}
                            >
                                {ROTULO_SITUACAO[s]}
                                <span className={`px-1.5 rounded-full text-[10px] tabular-nums ${aba === s ? 'bg-primary/10 text-primary dark:text-primary-light' : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-200'}`}>
                                    {contagem[s].toLocaleString('pt-BR')}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Filtros */}
                    <div className="flex flex-wrap items-center gap-2">
                        <select
                            value={motivoFiltro}
                            onChange={e => setMotivoFiltro(e.target.value)}
                            aria-label="Filtrar por motivo da perda"
                            className="px-3 py-1.5 text-sm rounded-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30"
                        >
                            <option value="">Todos os motivos</option>
                            {motivosNaLista.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                        <select
                            value={origemFiltro}
                            onChange={e => setOrigemFiltro(e.target.value)}
                            aria-label="Filtrar por origem do lead"
                            className="px-3 py-1.5 text-sm rounded-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30"
                        >
                            <option value="">Todas as origens</option>
                            {origens.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                        <div className="relative flex-1 min-w-[180px] sm:flex-none">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
                            <input
                                value={busca}
                                onChange={e => setBusca(e.target.value)}
                                placeholder="Buscar nome ou telefone"
                                aria-label="Buscar nome ou telefone"
                                className="w-full pl-8 pr-3 py-1.5 text-sm rounded-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30"
                            />
                        </div>
                    </div>

                    {/* Lista */}
                    {carregando && linhas.length === 0 ? (
                        <div className="py-16 flex justify-center"><LoadingSpinner /></div>
                    ) : daAba.length === 0 ? (
                        <div className="py-16 text-center">
                            <Megaphone size={32} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                            <p className="text-sm font-bold text-gray-600 dark:text-gray-300">Nada em "{ROTULO_SITUACAO[aba]}"</p>
                            <p className="text-xs text-gray-400 mt-1">
                                {aba === 'pronto' ? 'Quando a espera de um perdido terminar, ele aparece aqui.' : 'Mude a aba ou os filtros para ver outros.'}
                            </p>
                        </div>
                    ) : (
                        <ul className="rounded-2xl border border-gray-100 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                            {visiveis.map(l => (
                                <LinhaLista
                                    key={l.negociacao_id}
                                    l={l}
                                    agora={agora}
                                    situacao={situacaoRemarketing(l, agora)}
                                    onWhatsApp={() => enviarWhatsApp(l)}
                                    onVoltar={() => setAcao({ tipo: 'voltar', linha: l })}
                                    onAdiar={() => { setDiasAdiar(30); setAcao({ tipo: 'adiar', linha: l }); }}
                                    onDescartar={() => { setMotivoDescarte(MOTIVOS_DESCARTE[0]); setAcao({ tipo: 'descartar', linha: l }); }}
                                    onDesfazer={() => desfazerDescarte(l)}
                                    onAbrir={(id) => setNegociacaoAberta(id)}
                                />
                            ))}
                        </ul>
                    )}

                    {daAba.length > limite && (
                        <div className="flex justify-center">
                            <button
                                onClick={() => setLimite(n => n + POR_PAGINA)}
                                className="px-4 py-2 text-xs font-bold rounded-full border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                Mostrar mais ({(daAba.length - limite).toLocaleString('pt-BR')} restantes)
                            </button>
                        </div>
                    )}
                </>
            )}

            <ConfirmModal
                isOpen={acao?.tipo === 'voltar'}
                onClose={() => setAcao(null)}
                onConfirm={confirmarAcao}
                loading={salvando}
                variant="info"
                title="Voltar para o funil"
                confirmLabel="Voltar para o funil"
                message={`A negociação de ${acao?.linha.cliente_nome ?? ''} volta a ficar em andamento, na etapa em que estava, no seu nome. Use quando a pessoa responder e quiser seguir.`}
            />

            <ConfirmModal
                isOpen={acao?.tipo === 'adiar'}
                onClose={() => setAcao(null)}
                onConfirm={confirmarAcao}
                loading={salvando}
                variant="info"
                title="Adiar a reabordagem"
                confirmLabel="Adiar"
                message="A negociação sai das abas de trabalho e volta para Prontos na data escolhida."
            >
                <div className="flex flex-wrap gap-2 pt-1" role="radiogroup" aria-label="Adiar por quantos dias">
                    {OPCOES_ADIAR_DIAS.map(d => (
                        <button
                            key={d}
                            type="button"
                            role="radio"
                            aria-checked={diasAdiar === d}
                            onClick={() => setDiasAdiar(d)}
                            className={`px-3 py-1.5 text-xs font-bold rounded-full border transition-colors ${
                                diasAdiar === d
                                    ? 'bg-primary text-white border-primary'
                                    : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                            }`}
                        >
                            {d} dias
                        </button>
                    ))}
                </div>
            </ConfirmModal>

            <ConfirmModal
                isOpen={acao?.tipo === 'descartar'}
                onClose={() => setAcao(null)}
                onConfirm={confirmarAcao}
                loading={salvando}
                variant="warning"
                title="Descartar da lista"
                confirmLabel="Descartar"
                message="A negociação sai do remarketing. Dá para desfazer na aba Descartados."
            >
                <label className="block text-left text-xs font-bold uppercase text-gray-400 mb-1 pt-1" htmlFor="motivo-descarte">Por quê?</label>
                <select
                    id="motivo-descarte"
                    value={motivoDescarte}
                    onChange={e => setMotivoDescarte(e.target.value)}
                    className="w-full px-3 py-2 text-sm rounded-lg bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                    {MOTIVOS_DESCARTE.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                {descarteBloqueiaContato(motivoDescarte) && (
                    <p className="mt-2 text-left text-xs text-gray-500 dark:text-gray-400">
                        Não volta para a lista nem se for perdida de novo.
                    </p>
                )}
            </ConfirmModal>

            <NegotiationDetailModal
                isOpen={negociacaoAberta !== null}
                onClose={() => setNegociacaoAberta(null)}
                negotiationId={negociacaoAberta}
                onUpdate={carregar}
            />
        </div>
    );
};

interface LinhaListaProps {
    l: LinhaRemarketing;
    agora: Date;
    situacao: SituacaoRemarketing;
    onWhatsApp: () => void;
    onVoltar: () => void;
    onAdiar: () => void;
    onDescartar: () => void;
    onDesfazer: () => void;
    onAbrir: (negociacaoId: number) => void;
}

const botao = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-full transition-colors';
const botaoNeutro = `${botao} border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700`;

const LinhaLista: React.FC<LinhaListaProps> = ({ l, agora, situacao, onWhatsApp, onVoltar, onAdiar, onDescartar, onDesfazer, onAbrir }) => {
    const ativa = situacao === 'pronto' || situacao === 'aguardando' || situacao === 'em_nutricao';
    const telefone = formatarTelefone(l.telefone);
    const podeMandar = !!linkWhatsApp(l.telefone, 'x');

    return (
        <li className="flex flex-col lg:flex-row lg:items-center gap-3 px-4 py-3">
            <div className="min-w-0 flex-1 space-y-0.5">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <p className="text-sm font-bold text-gray-900 dark:text-white truncate max-w-full">{l.cliente_nome || 'Sem nome'}</p>
                    <span className={`px-2 py-0.5 rounded-full border text-[10px] font-bold ${COR_MOTIVO[l.motivo_perda] ?? COR_MOTIVO_PADRAO}`}>
                        {l.motivo_perda}
                    </span>
                    {l.origem && <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500">{l.origem}</span>}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                    {telefone || <span className="text-red-500">telefone inválido</span>}
                    {' · '}perdido {haDias(l.perdido_em, agora)}
                    {l.etapa_funil ? ` em ${l.etapa_funil}` : ''}
                    {l.cidade ? ` · ${l.cidade}` : ''}
                </p>
                {situacao === 'aguardando' && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <CalendarClock size={12} aria-hidden="true" /> Reabordar a partir de {dataCurta(l.reabordar_em)}
                    </p>
                )}
                {l.tentativas > 0 && ativa && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        {l.tentativas === 1 ? '1 mensagem' : `${l.tentativas} mensagens`}
                        {l.ultima_tentativa_em ? `, última ${haDias(l.ultima_tentativa_em, agora)}` : ''}
                    </p>
                )}
                {situacao === 'recuperado' && l.recuperado_via && (
                    <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                        {ROTULO_VIA[l.recuperado_via]} · {dataCurta(l.recuperado_em)}
                    </p>
                )}
                {situacao === 'descartado' && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        Descartada: {l.descarte_motivo || 'sem motivo'} · {dataCurta(l.updated_at)}
                    </p>
                )}
                {l.observacoes_perda && (
                    <p className="text-xs italic text-gray-400 dark:text-gray-500 line-clamp-1" title={l.observacoes_perda}>
                        “{l.observacoes_perda}”
                    </p>
                )}
            </div>

            <div className="flex flex-wrap items-center gap-1.5 lg:justify-end lg:shrink-0">
                {ativa && (
                    <>
                        <button
                            onClick={onWhatsApp}
                            disabled={!podeMandar}
                            title={podeMandar ? 'Abre o WhatsApp com a mensagem pronta e conta como tentativa' : 'Telefone inválido'}
                            className={`${botao} bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed`}
                        >
                            <MessageCircle size={13} /> WhatsApp
                        </button>
                        <button onClick={onVoltar} className={`${botao} bg-primary text-white hover:opacity-90`} title="A pessoa respondeu e quer seguir">
                            <Undo2 size={13} /> Voltar ao funil
                        </button>
                        <button onClick={onAdiar} className={botaoNeutro} title="Pediu para falar mais tarde">
                            <CalendarClock size={13} /> Adiar
                        </button>
                        <button onClick={onDescartar} className={botaoNeutro} title="Tirar da lista">
                            <Ban size={13} /> Descartar
                        </button>
                    </>
                )}
                {situacao === 'descartado' && (
                    <button onClick={onDesfazer} className={botaoNeutro}>
                        <Undo2 size={13} /> Voltar para a fila
                    </button>
                )}
                {situacao === 'recuperado' && l.recuperado_via === 'novo_lead' && l.recuperado_negociacao_id && (
                    <button onClick={() => onAbrir(l.recuperado_negociacao_id!)} className={botaoNeutro}>
                        <ExternalLink size={13} /> Abrir a nova
                    </button>
                )}
                <button onClick={() => onAbrir(l.negociacao_id)} className={botaoNeutro} title="Abrir a negociação">
                    <ExternalLink size={13} /> Abrir
                </button>
            </div>
        </li>
    );
};

export default RemarketingView;
